import {ChangeDetectionStrategy, Component, Input, OnInit, ViewChild} from '@angular/core';
import {TipRadioGroupComponent} from '../tip-radio-group/tip-radio-group.component';
import {PopoverController} from '@ionic/angular';
import { DatePickUpTime, DateTime, TimeInterval } from '../../../../core/models/order.model';
import {DatePipe, LowerCasePipe} from '@angular/common';
import {Time, WorkDay} from '../../../../core/models/restaurant.model';
import { Router } from '@angular/router';
import {FormatDataService} from '../../../../core/services/format-data.service';
import {CartStore} from '../../../../core/stores/cart.store';
import {RestaurantStore} from '../../../../core/stores/restaurant.store';
import { TimeService } from '../../../../core/services/time.service';
import {parseDuration} from '../../../../core/dto/restaurant.dto';

const PAGINATE_DAYS:number = 30;

@Component({
  selector: 'app-pick-up-date-time',
  templateUrl: './pick-up-date-time.component.html',
  styleUrls: ['./pick-up-date-time.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PickUpDateTimeComponent implements OnInit {
  @Input() initialDate: Date;
  @Input() initialTimeInterval: TimeInterval;
  @Input() hoursOfOperation: WorkDay[];
  @Input() interval: number;
  @Input() deliveryInterval: number;
  @Input() pickupDaysInAdvance: any;
  @Input() estimatedTimeInMinutes: number;
  @Input() hideETA: boolean;
  @Input() disableASAP: boolean;
  @Input() selectASAP: boolean;
  @Input() acceptingOrders: boolean;
  @Input() timezone: string;
  @Input() disableTodayOrders: boolean;
  @Input() showWindowEndTime: boolean;
  @Input() isCityFlavor: boolean;
  @Input() isFlavorShifts: boolean;
  @Input() estimateDuration: string;
  @Input() selectedDeliveryLocation: string;
  @Input() locationId: string;
  @Input() useFlavorShifts: boolean;
  @Input() shifts:any[];
  @Input() isDelivery: boolean;
  public intervals: TimeInterval[] = [];
  public asap: boolean;
  public dateItems: Date[] = [];
  public nextDays: number = PAGINATE_DAYS;
  public date: Date;
  public timeInterval: TimeInterval;
  public fullDayPickUpAvailable: boolean;
  @ViewChild('dateTipRadio', {static: false}) dateTipRadio: TipRadioGroupComponent;

  public get asapText(): string {
    if (this.estimateDuration && parseDuration(this.estimateDuration)) {
      return `ASAP (~${parseDuration(this.estimateDuration)})`;
    }
    if (this.isDelivery) {
      const hours = Math.floor(this.deliveryInterval / 60);
      const minutes = this.deliveryInterval % 60;
      return `ASAP: ${hours > 0 ? `${hours} hour` : ''} ${minutes} mins`;
    }
    return this.hideETA || !this.estimatedTimeInMinutes ? 'ASAP' : `ASAP (~${this.estimatedTimeInMinutes} min)`;
  }

  public get selectStart(): boolean {
    return this.asap && (this.selectASAP || !!this.cartStore.pickupAt);
  }

  private get currentDayWork(): WorkDay {
    return this.timeService.getWorkDay(this.date);
  }

  private get todayDayWork(): WorkDay {
    return this.timeService.getWorkDay(new Date());
  }

  constructor(public popoverController: PopoverController,
              private cartStore: CartStore,
              private datePipe: DatePipe,
              private lowerCasePipe: LowerCasePipe,
              private router: Router,
              private restaurantStore: RestaurantStore,
              private formatDataService: FormatDataService,
              private timeService: TimeService
  ) {
  }

  public async ngOnInit() {
    this.prepareDates();
    this.date = this.timeService.initDate(this.dateItems);
    this.setIntervals();
    this.fullDayPickUpAvailable = this.timeService.fullDayPickUpAvailable;
    this.nextDays = this.timeService.pickupDaysInAdvance < PAGINATE_DAYS ? this.timeService.pickupDaysInAdvance : PAGINATE_DAYS;
    if(this.initialDate){
      this.date = this.initialDate
    } else {
      this.date = this.timeService.initDate(this.dateItems);
    }
    this.timeInterval = this.initTimeInterval();
    this.timeCityFlavor();
  }

  public prepareDates() {
    const startDate = this.getStartDate();
    this.loadDates(startDate);
  }

  public getStartDate(): Date {
    if (this.nextDays > PAGINATE_DAYS && !this.useFlavorShifts) {
      const lastDate = this.dateItems[this.dateItems.length - 1];
      return this.addOneDayToDate(lastDate);
    }
    let startDate = this.timeService.getStartDate();
    if (this.intervals.length && startDate < this.intervals[0].start) {
      startDate = this.intervals[0].start;
    }
    return startDate;
  }

  public loadDates(date: Date) {
    if(this.useFlavorShifts){
      const uniqueDays = new Set<string>();
      this.shifts.forEach(shift => {
        if(shift.day){
          uniqueDays.add(shift.day)
        }
      })
      this.dateItems = Array.from(uniqueDays).map((day)=> new Date(day));
    } else {
      this.dateItems = [...this.dateItems, ...this.timeService.nextWorkDaysInAdvance(date, this.nextDays)];
    }
  }

  public addOneDayToDate(date: Date): Date {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + 1);
    return newDate;
}

  private initTimeInterval() {
    if (!this.initialTimeInterval) {
      return;
    }
    return this.intervals.find(interval => {
        return this.isEqualTime(this.initialTimeInterval.start, interval.start)
          && this.isEqualTime(this.initialTimeInterval.end, interval.end);
      }
    );
  }

  private isEqualTime(date1: Date, date2: Date): boolean {
    return this.datePipe.transform(date1, 'HH:mm') === this.datePipe.transform(date2, 'HH:mm');
  }

  public setAsap() {
    this.popoverController.dismiss(
      new DatePickUpTime(
        new DateTime(this.date, null),
        true,
        this.fullDayPickUpAvailable
      ));
  }

  isEqualInterval = (interval1: TimeInterval, interval2: TimeInterval) => {
    if (!interval1 || !interval2) {
      return;
    }
    return this.isEqualTime(interval1.start, interval2.start)
      && this.isEqualTime(interval1.end, interval2.end);
  }

  public isToday(date: Date) {
    if (!date) {
      return;
    }
    const today = new Date();
    return today.toDateString() === date.toDateString();
  }

  public isThisMonth(date: Date) {
    if (!date) {
      return;
    }
    const today = new Date();
    return today.getMonth() === date.getMonth() && date.getFullYear() === today.getFullYear();
  }

  public hasAsap(date: Date): boolean {
    if (this.isCityFlavor && this.isDelivery) {
      return false;
    } else if(this.useFlavorShifts) {
      return false
    } else if (this.intervals.length === 0 && !this.fullDayPickUpAvailable) {
      return false;
    } else if (this.isDelivery && this.isToday(date) && this.acceptingOrders) {
      return true;
    } else if (this.disableASAP || !this.isToday(date)) {
      return false;
    }
    const workDay = this.timeService.getWorkDay(date);
    return this.timeService.isAsap(workDay);
  }

  public chooseFullDay(): void {
    this.asap = false;
    const workDay = this.timeService.getWorkDay(this.date);
    const startTimeHour = workDay.startTime.length ? workDay.startTime[0].time.hour : 0;
    const startTimeMin = workDay.startTime.length ? workDay.startTime[0].time.minute : 0;
    const timeFrom = new Date(this.date);
    timeFrom.setHours(startTimeHour);
    timeFrom.setMinutes(startTimeMin);
    const timeTo = new Date(this.date);
    timeTo.setHours(23);
    timeTo.setMinutes(59);
    this.timeInterval = new TimeInterval(timeFrom, timeTo);
    this.popoverController.dismiss(
      new DatePickUpTime(
        new DateTime(this.date, this.timeInterval),
        this.asap,
        this.fullDayPickUpAvailable
      ));
  }

  public dateChanged(): void {
    if (!this.date) {
      return;
    }
    if (this.isCityFlavor) {
      this.timeCityFlavor();
      return;
    }
    if(this.useFlavorShifts){
      const filteredShifts = this.shifts.filter(shift => {
        const shiftDate = new Date(shift.day);
        return shiftDate.toDateString() === this.date.toDateString();
      });

      this.intervals = this.timeService.generateTimeIntervalsCF(filteredShifts, this.interval, this.isDelivery, this.selectedDeliveryLocation);
      return;
    }
    if (this.isToday(this.date)) {
      let now = new Date();
      if (!this.fullDayPickUpAvailable) {
        now.setMinutes(now.getMinutes() + this.estimatedTimeInMinutes);
      }
      if (this.estimateDuration && this.estimateDuration !== 'PT0S') {
        const duration = this.formatDataService.getIntervalFromDurationMili(this.estimateDuration);
        now = new Date(now.getTime() + duration);
        this.intervals = this.timeService.generateIntervals(now, duration);
      } else {
        this.intervals = this.timeService.generateIntervals(now);
      }
    } else {
      this.asap = false;
      this.intervals = this.timeService.generateIntervals(this.date);
      this.fullDayPickUpAvailable = this.timeService.fullDayPickUpAvailable;
    }
    this.timeInterval = null;
    if (this.fullDayPickUpAvailable) {
      this.chooseFullDay();
    }
  }

  public selectTime() {
    this.asap = false;
    this.popoverController.dismiss(
      new DatePickUpTime(
        new DateTime(
          this.date,
          this.timeInterval
        ),
        this.asap,
      )
    );
  }

  public formatIntervals({start, end}: TimeInterval): string {
    return this.showWindowEndTime ? `${this.formatTime(start)} - ${this.formatTime(end)}` : this.formatTime(start);
  }

  public formatTime(time: Date): string {
    return this.lowerCasePipe.transform(this.datePipe.transform(time, 'h:mmaaaaa'));
  }

  public async next() {
    this.getNextDates();
    await this.dateTipRadio.next();
  }

  public getNextDates() {
    if (this.timeService.pickupDaysInAdvance > this.nextDays && !this.useFlavorShifts) {
      this.nextDays = this.timeService.pickupDaysInAdvance - (this.nextDays + PAGINATE_DAYS) > 0 ?
        this.nextDays + PAGINATE_DAYS :
        this.nextDays + (this.timeService.pickupDaysInAdvance - this.nextDays);
      this.prepareDates();
    }
  }

  public async prev() {
    await this.dateTipRadio.prev();
  }

  private timeCityFlavor(): void {
    if (this.isCityFlavor && this.isDelivery) {
      this.disableASAP = true;
      this.asap = false;
      if (this.selectedDeliveryLocation
        && this.selectedDeliveryLocation.toLowerCase() === 'end of line'
        && this.locationId === 'L3QVX574ASQ7K') {
        this.intervals = this.timeService.generateIntervalsForCityFlavorSpecial(
          this.date,
          this.restaurantStore.estimateDeliveryTime * 60000
        );
        return;
      }
      this.intervals = this.timeService.generateIntervalsForCityFlavor(
        this.date,
        this.restaurantStore.estimateDeliveryTime * 60000
      );
    }
  }

  private setIntervals(): void {
    let startDate = this.initialDate
    if(this.useFlavorShifts) {
      const filteredShifts = this.shifts.filter(shift => {
        const shiftDate = new Date(shift.day);
        return shiftDate.toDateString() === startDate.toDateString();
      });
      this.intervals = this.timeService.generateTimeIntervalsCF(filteredShifts, this.interval, this.isDelivery, this.selectedDeliveryLocation);
    } else if (this.estimateDuration && this.estimateDuration !== 'PT0S') {
      let now = new Date() < startDate ? startDate : new Date();
      const duration = this.formatDataService.getIntervalFromDurationMili(this.estimateDuration);
      now = new Date(now.getTime() + duration);
      this.intervals = this.timeService.generateIntervals(now, duration);
    } else if (this.timeService.generateIntervals()) {
      this.date = this.timeService.getStartDate();
      this.dateChanged();
    }
  }
}
